import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

export default function Servizi() {
  const [expandedService, setExpandedService] = useState<string | null>(null);

  // Update document head for SEO
  useEffect(() => {
    document.title = 'Fortal Society | Community, Formazione, PAMM, Copy Trading';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Servizi completi per trader: community esclusiva, formazione professionale, gestione PAMM e copy trading. Soluzioni per ogni livello di esperienza nel trading.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'servizi trading, community trading, formazione trading, PAMM, copy trading, gestione portafoglio, Fortal Society servizi');
    }
    
    // Add canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/servizi`;
    
    // Add Schema.org JSON-LD for services page
    const existingScript = document.querySelector('script[data-schema="services"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Servizi di Trading Professionali",
      "description": "Servizi completi per trader: community esclusiva, formazione professionale, gestione PAMM e copy trading. Soluzioni per ogni livello di esperienza nel trading.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/servizi`,
      "mainEntity": [
        {
          "@type": "Service",
          "name": "Community Telegram Esclusiva",
          "description": "Accesso alla community privata di trader professionisti con analisi giornaliere, setup operativi e supporto continuo",
          "provider": {
            "@type": "Organization",
            "name": "Fortal Society",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}`
          },
          "serviceType": "Trading Community",
          "areaServed": "IT",
          "offers": {
            "@type": "Offer",
            "price": "97",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
          }
        },
        {
          "@type": "Service",
          "name": "Formazione Completa",
          "description": "Corsi strutturati dal principiante all'avanzato con live sessions, materiali esclusivi e supporto continuo",
          "provider": {
            "@type": "Organization",
            "name": "Fortal Society",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}`
          },
          "serviceType": "Trading Education",
          "areaServed": "IT"
        },
        {
          "@type": "Service",
          "name": "PAMM Account",
          "description": "Gestione professionale del capitale con performance verificate e trasparenza totale sui risultati",
          "provider": {
            "@type": "Organization",
            "name": "Fortal Society",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}`
          },
          "serviceType": "Portfolio Management",
          "areaServed": "IT"
        },
        {
          "@type": "Service",
          "name": "Copy Trading",
          "description": "Replica automatica delle operazioni dei trader professionisti con controllo completo del rischio",
          "provider": {
            "@type": "Organization",
            "name": "Fortal Society",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}`
          },
          "serviceType": "Copy Trading",
          "areaServed": "IT"
        }
      ],
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Servizi",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/servizi`
          }
        ]
      }
    };
    
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.setAttribute('data-schema', 'services');
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);
    
    return () => {
      // Cleanup function to remove schema when component unmounts
      const script = document.querySelector('script[data-schema="services"]');
      if (script) {
        script.remove();
      }
    };
  }, []);

  const services = [
    {
      id: 'community',
      icon: 'ri-team-line',
      title: 'Community Gratuita',
      description: 'Una community esclusiva e gratuita che riunisce oltre 800 membri tra trader, investitori e appassionati del settore. Un luogo dove confrontarsi, condividere analisi, imparare ogni giorno e crescere insieme. All\'interno troverai topic tematici, aggiornamenti di mercato e spunti di formazione continua. Unisciti subito e vivi da dentro la mentalità Fortal Society.',
      features: [
        'Oltre 800 membri attivi',
        'Topic tematici specializzati',
        'Aggiornamenti di mercato quotidiani',
        'Spunti di formazione continua',
        'Confronto con trader esperti'
      ],
      available: true,
      popular: true,
      detailedContent: {
        overview: 'La Community Gratuita di Fortal Society è il punto di partenza ideale per entrare nel nostro ecosistema. Con oltre 800 membri attivi tra trader, investitori e appassionati, rappresenta un ambiente stimolante dove crescere e confrontarsi quotidianamente.',
        benefits: [
          'Accesso gratuito e immediato alla community Telegram',
          'Oltre 800 membri attivi tra trader professionali e aspiranti',
          'Topic tematici organizzati per argomenti specifici',
          'Aggiornamenti di mercato quotidiani e analisi condivise',
          'Spunti di formazione continua e materiali educativi',
          'Confronto diretto con trader esperti e mentori',
          'Networking con investitori e professionisti del settore',
          'Supporto della community per domande e dubbi',
          'Accesso agli archivi con contenuti formativi passati'
        ],
        process: 'L\'iscrizione è completamente gratuita e immediata. Una volta entrato, potrai accedere a tutti i topic tematici, partecipare alle discussioni quotidiane e beneficiare dell\'esperienza condivisa di centinaia di trader. La community è attiva 24/7 con moderatori dedicati.',
        requirements: 'Nessun requisito particolare. La community è aperta a tutti: principianti, trader esperti, investitori e semplici appassionati del settore finanziario. L\'unico requisito è la voglia di imparare e crescere insieme.'
      }
    },
    {
      id: 'formazione',
      icon: 'ri-graduation-cap-line',
      title: 'Formazione Base',
      description: 'Un percorso pensato per chi parte da zero e vuole entrare nel mondo del trading con basi solide. La Formazione Base Fortal Society ti accompagna passo dopo passo nello sviluppo delle competenze tecniche fondamentali, spiegando in modo chiaro e progressivo le strategie operative utilizzate dai professionisti. Ideale per neofiti che vogliono comprendere a fondo i meccanismi del mercato prima di operare in autonomia.',
      features: [
        'Corso base strutturato',
        'Video lezioni HD',
        'Materiale didattico completo',
        'Esercitazioni pratiche',
        'Supporto durante il percorso'
      ],
      available: true,
      popular: false,
      detailedContent: {
        overview: 'La Formazione Base Fortal Society è un percorso strutturato e progressivo, specificamente progettato per chi muove i primi passi nel mondo del trading. Il nostro approccio didattico parte dalle basi assolute e costruisce gradualmente le competenze necessarie per operare sui mercati finanziari con consapevolezza e disciplina.',
        benefits: [
          'Percorso strutturato da zero: nessuna conoscenza pregressa richiesta',
          'Spiegazioni chiare e progressive delle strategie professionali',
          'Oltre 40 ore di video lezioni in alta definizione',
          'Materiale didattico scaricabile: PDF, template, checklist operative',
          'Accesso a vita a tutti i contenuti e aggiornamenti futuri',
          'Esercitazioni pratiche su piattaforme demo per applicare subito le conoscenze',
          'Sessioni di Q&A mensili con i formatori per chiarire ogni dubbio',
          'Supporto continuo durante tutto il percorso formativo',
          'Accesso prioritario a nuovi corsi e masterclass avanzate'
        ],
        process: 'Il corso è strutturato in moduli progressivi che partono dalle basi assolute: cos\'è il trading, come funzionano i mercati, fino ad arrivare alle strategie operative concrete utilizzate dai professionisti. Ogni modulo include video lezioni dettagliate, materiale di supporto ed esercitazioni pratiche. Al termine di ogni sezione, quiz e test verificano l\'apprendimento prima di passare al livello successivo.',
        requirements: 'Non sono necessarie conoscenze pregresse nel trading o nei mercati finanziari. Il corso è pensato per principianti assoluti. È richiesto un computer o tablet per seguire le lezioni, una connessione internet stabile e la voglia di imparare e mettersi in gioco con le esercitazioni pratiche.'
      }
    },
    {
      id: 'formazione-premium',
      icon: 'ri-vip-crown-line',
      title: 'Formazione Premium - MastermindFX',
      description: 'La Formazione MastermindFX rappresenta il livello professionale dell\'Academy Fortal Society. Un percorso intensivo e completo, affiancato costantemente da un coach dedicato, con accesso a video formativi avanzati, zoom call private e di gruppo, supporto 1-to-1 personalizzato e chat riservata con aggiornamenti esclusivi. Perfetta per chi vuole fare del trading una vera professione, accelerando risultati e mentalità operativa.',
      features: [
        'Coach dedicato personale',
        'Video formativi avanzati',
        'Zoom call private e di gruppo',
        'Supporto 1-to-1 personalizzato',
        'Chat riservata esclusiva'
      ],
      available: true,
      popular: false,
      detailedContent: {
        overview: 'La Formazione MastermindFX è il vertice dell\'offerta formativa di Fortal Society. Un percorso intensivo e professionale che trasforma aspiranti trader in professionisti del settore attraverso un accompagnamento costante e personalizzato.',
        benefits: [
          'Coach dedicato assegnato personalmente per tutto il percorso',
          'Accesso esclusivo a video formativi avanzati e strategie professionali',
          'Zoom call private settimanali per sessioni 1-to-1 personalizzate',
          'Zoom call di gruppo mensili con altri membri MastermindFX',
          'Supporto continuo e personalizzato via chat dedicata',
          'Chat riservata esclusiva con aggiornamenti in tempo reale',
          'Accesso prioritario a nuove strategie e metodologie',
          'Analisi personalizzate del proprio trading journal',
          'Mentoring diretto per sviluppare la mentalità vincente',
          'Networking esclusivo con trader professionali di alto livello'
        ],
        process: 'Dopo l\'iscrizione, viene assegnato un coach dedicato che seguirà personalmente tutto il percorso. Le sessioni 1-to-1 permettono di lavorare su aspetti specifici, mentre le call di gruppo creano sinergie con altri trader del programma. Il supporto è continuo attraverso la chat riservata.',
        requirements: 'Questo programma è riservato a chi è seriamente intenzionato a fare del trading una professione. È consigliabile aver completato la Formazione Base o avere già esperienza nel trading. Richiede impegno costante e dedizione al percorso di crescita.'
      }
    },
    {
      id: 'pamm',
      icon: 'ri-funds-line',
      title: 'PAMM',
      description: 'Il Servizio PAMM (Percentage Allocation Money Management) di Fortal Society offre una soluzione di gestione passiva del capitale, completamente gratuita, pensata per chi desidera ottenere rendimenti costanti con rischio minimo e una visione a lungo termine. Ogni cliente deposita i propri fondi in un conto personale collegato al trader principale Fortal Society, che gestisce l\'intero capitale tramite strategie professionali e consolidate.',
      features: [
        'Gestione passiva del capitale',
        'Servizio completamente gratuito',
        'Rischio minimo controllato',
        'Distribuzione automatica profitti',
        'Dashboard di controllo personale'
      ],
      available: true,
      popular: false,
      detailedContent: {
        overview: 'Il servizio PAMM di Fortal Society rappresenta una soluzione innovativa per chi vuole investire nei mercati finanziari senza dover operare direttamente. Attraverso questo sistema, il tuo capitale viene gestito da trader professionali con strategie consolidate e testate.',
        benefits: [
          'Gestione professionale del capitale da parte di trader certificati Fortal Society',
          'Servizio completamente gratuito senza commissioni di gestione',
          'Possibilità di operare con capitali più grandi attraverso il pooling',
          'Riduzione significativa del rischio di perdita attraverso diversificazione',
          'Conto unico che elimina il rischio di performance diverse',
          'Sistema 100% automatico che non richiede competenze specifiche',
          'Dashboard personale per monitorare investimenti e performance',
          'Possibilità di aggiungere o ritirare fondi in qualsiasi momento',
          'Distribuzione automatica dei profitti in base alla percentuale investita',
          'Trasparenza totale su tutte le operazioni effettuate'
        ],
        process: 'Cos\'è una PAMM? Una PAMM (Percentage Allocation Management Module) è un tipo di conto gestito nel trading, in particolare nel Forex, dove un trader professionista (gestore) opera con i propri fondi e quelli degli investitori all\'interno di un unico conto. I profitti vengono distribuiti automaticamente in percentuale, in base all\'importo investito da ciascun partecipante. Dalla dashboard potrete aggiungere o ritirare i soldi, che verranno aggiunti o sottratti al conto collegato.',
        requirements: 'Investimento minimo variabile in base alla strategia scelta. È necessario completare il processo di onboarding e collegare un conto di trading compatibile. Il servizio è ideale per chi desidera accedere ai mercati finanziari in modo semplice, sicuro e professionale.'
      }
    },
    {
      id: 'copytrading',
      icon: 'ri-file-copy-line',
      title: 'Copy Trading',
      description: 'Copia automaticamente le nostre operazioni sul tuo conto. Perfetto per chi vuole seguire i nostri trade.',
      features: [
        'Copia automatica dei trade',
        'Controllo del rischio',
        'Statistiche dettagliate',
        'Gestione personalizzabile',
        'Supporto tecnico dedicato'
      ],
      available: false,
      popular: false,
      detailedContent: {
        overview: 'Il servizio di Copy Trading permetterà di replicare automaticamente le nostre operazioni direttamente sul tuo conto di trading. Una soluzione tecnologicamente avanzata per seguire le nostre strategie in tempo reale.',
        benefits: [
          'Copia automatica e istantanea di tutte le nostre operazioni',
          'Controllo personalizzabile del rischio e della size delle posizioni',
          'Statistiche dettagliate su performance e drawdown',
          'Possibilità di escludere specifici strumenti o strategie',
          'Supporto tecnico dedicato per configurazione e ottimizzazione',
          'Compatibilità con le principali piattaforme di trading',
          'Notifiche real-time su aperture e chiusure posizioni'
        ],
        process: 'Una volta configurato il collegamento tra il nostro conto master e il tuo conto, tutte le operazioni verranno replicate automaticamente. Potrai impostare parametri personalizzati per il risk management e monitorare le performance attraverso dashboard dedicata.',
        requirements: 'Sarà necessario un conto presso uno dei broker partner e l\'installazione del software di copy trading. Capitale minimo consigliato: €5.000 per una corretta diversificazione.'
      }
    }
  ];

  const toggleService = (serviceId: string) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  const faqs = [
    {
      question: 'Come accedo alla Community Gratuita?',
      answer: 'L\'accesso alla Community Gratuita è immediato e senza costi. Ti basta richiedere l\'invito e potrai subito unirti agli oltre 800 membri per condividere analisi, ricevere aggiornamenti di mercato e crescere insieme.'
    },
    {
      question: 'Qual è la differenza tra Formazione Base e MastermindFX?',
      answer: 'La Formazione Base è perfetta per principianti e copre le fondamenta del trading. MastermindFX è il livello professionale con coach dedicato, supporto 1-to-1, call private e chat esclusiva per chi vuole fare del trading una professione.'
    },
    {
      question: 'Come funziona esattamente il servizio PAMM?',
      answer: 'Il PAMM è un sistema dove depositi i tuoi fondi in un conto collegato al trader principale Fortal Society. I profitti vengono distribuiti automaticamente in percentuale. È completamente gratuito e puoi aggiungere o ritirare fondi quando vuoi dalla dashboard.'
    },
    {
      question: 'Quali sono i vantaggi del PAMM rispetto al trading individuale?',
      answer: 'Con il PAMM puoi operare con capitali più grandi, ridurre il rischio attraverso la diversificazione, beneficiare di un conto unico senza disparità di performance, e tutto è 100% automatico senza richiedere competenze specifiche o tempo da dedicare.'
    },
    {
      question: 'Quando sarà disponibile il copy trading?',
      answer: 'Il servizio di copy trading è attualmente in fase di sviluppo. Sarà disponibile nei prossimi mesi con funzionalità avanzate di gestione del rischio e personalizzazione.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 relative">
      <Navbar />
      
      <div className="pt-20">
        {/* Hero Section */}
        <section 
          className="relative py-20 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.8)), url('https://public.readdy.ai/ai/img_res/82841c67c566921ed758b1ef570980a4.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/20 to-slate-950"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 animate-slide-in-up">
              I Nostri{' '}
              <span className="text-gradient-gold animate-pulse-gold glow-gold-strong">
                Servizi
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto animate-slide-in-up delay-200">
              Soluzioni complete per trader di ogni livello. 
              Dalla community esclusiva alla gestione professionale del capitale.
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              {services.map((service, index) => (
                <div key={service.id} className="relative">
                  {service.popular && (
                    <div className="absolute -top-4 left-8 z-10">
                      <span className="bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 px-4 py-1 rounded-full text-sm font-bold animate-pulse-gold glow-gold">
                        Più Popolare
                      </span>
                    </div>
                  )}
                  
                  <Card 
                    variant={service.popular ? "gradient" : "glass"} 
                    className={`hover-lift animate-bounce-in transition-all duration-300 ${service.popular ? 'glow-gold-strong' : ''} ${!service.available ? 'opacity-75' : ''} ${expandedService === service.id ? 'ring-2 ring-amber-400/50' : ''}`}
                    style={{animationDelay: `${index * 0.2}s`}}
                  >
                    <div 
                      className="cursor-pointer"
                      onClick={() => toggleService(service.id)}
                    >
                      <div className="flex flex-col md:flex-row items-start gap-6">
                        <div className="flex-shrink-0">
                          <div className={`w-16 h-16 flex items-center justify-center rounded-2xl animate-float ${
                            service.popular 
                              ? 'bg-gradient-to-br from-slate-900/20 to-slate-800/20' 
                              : 'bg-gradient-to-br from-amber-400/20 to-yellow-500/20'
                          } glow-gold`}>
                            <i className={`${service.icon} text-3xl ${service.popular ? 'text-amber-400' : 'text-amber-400'}`}></i>
                          </div>
                        </div>

                        <div className="flex-1">
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-2xl font-bold text-white">{service.title}</h3>
                              <div className="w-6 h-6 flex items-center justify-center">
                                <i className={`ri-arrow-${expandedService === service.id ? 'up' : 'down'}-s-line text-amber-400 transition-transform duration-300`}></i>
                              </div>
                            </div>
                            <p className="text-slate-300 mb-4">{service.description}</p>
                            
                            {!service.available && (
                              <div className="mb-4">
                                <span className="bg-amber-500/20 border border-amber-500/30 text-amber-400 px-3 py-1 rounded-full text-sm font-medium animate-pulse-gold">
                                  Coming Soon
                                </span>
                              </div>
                            )}
                          </div>

                          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                            {service.features.map((feature, featureIndex) => (
                              <li key={featureIndex} className="flex items-start">
                                <div className="w-5 h-5 flex items-center justify-center mr-3 mt-0.5">
                                  <i className="ri-check-line text-amber-400"></i>
                                </div>
                                <span className="text-slate-300 text-sm">{feature}</span>
                              </li>
                            ))}
                          </ul>

                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                              {service.available ? (
                                <a 
                                  href={
                                    service.id === 'community' ? 'https://t.me/communityfreefortal' : 
                                    service.id === 'pamm' ? 'https://t.me/+Qp7UCEncZ3Y5MzBk' : 
                                    '#'
                                  } 
                                  target={service.id === 'community' || service.id === 'pamm' ? '_blank' : '_self'}
                                  rel={service.id === 'community' || service.id === 'pamm' ? 'noopener noreferrer' : ''}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Button 
                                    variant={service.popular ? "secondary" : "primary"} 
                                    className="hover-lift"
                                    disabled={!service.available}
                                  >
                                    {service.id === 'community' && 'Entra in Community Gratuita'}
                                    {service.id === 'formazione' && 'Inizia la Formazione'}
                                    {service.id === 'formazione-premium' && 'Accedi a MastermindFX'}
                                    {service.id === 'pamm' && 'Richiedi Info PAMM'}
                                    {service.id === 'copytrading' && 'Coming Soon'}
                                  </Button>
                                </a>
                              ) : (
                                <Button variant="ghost" disabled onClick={(e) => e.stopPropagation()}>
                                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                                    <i className="ri-lock-line"></i>
                                  </div>
                                  Prossimamente
                                </Button>
                              )}
                            </div>
                            
                            <span className="text-amber-400 text-sm font-medium">
                              Clicca per {expandedService === service.id ? 'chiudere' : 'approfondire'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      <div className={`overflow-hidden transition-all duration-500 ${expandedService === service.id ? 'max-h-[2000px] opacity-100 mt-8' : 'max-h-0 opacity-0'}`}>
                        <div className="border-t border-amber-200/20 pt-8">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Overview */}
                            <div>
                              <h4 className="text-xl font-bold text-white mb-4 flex items-center">
                                <div className="w-6 h-6 flex items-center justify-center mr-3">
                                  <i className="ri-information-line text-amber-400"></i>
                                </div>
                                Panoramica
                              </h4>
                              <p className="text-slate-300 mb-6">{service.detailedContent.overview}</p>

                              <h4 className="text-xl font-bold text-white mb-4 flex items-center">
                                <div className="w-6 h-6 flex items-center justify-center mr-3">
                                  <i className="ri-settings-line text-amber-400"></i>
                                </div>
                                Come Funziona
                              </h4>
                              <p className="text-slate-300">{service.detailedContent.process}</p>
                            </div>

                            {/* Benefits & Requirements */}
                            <div>
                              <h4 className="text-xl font-bold text-white mb-4 flex items-center">
                                <div className="w-6 h-6 flex items-center justify-center mr-3">
                                  <i className="ri-star-line text-amber-400"></i>
                                </div>
                                Vantaggi Inclusi
                              </h4>
                              <ul className="space-y-3 mb-6">
                                {service.detailedContent.benefits.map((benefit, benefitIndex) => (
                                  <li key={benefitIndex} className="flex items-start">
                                    <div className="w-5 h-5 flex items-center justify-center mr-3 mt-0.5">
                                      <i className="ri-check-double-line text-green-400"></i>
                                    </div>
                                    <span className="text-slate-300 text-sm">{benefit}</span>
                                  </li>
                                ))}
                              </ul>

                              <h4 className="text-xl font-bold text-white mb-4 flex items-center">
                                <div className="w-6 h-6 flex items-center justify-center mr-3">
                                  <i className="ri-file-list-line text-amber-400"></i>
                                </div>
                                Requisiti
                              </h4>
                              <p className="text-slate-300">{service.detailedContent.requirements}</p>
                            </div>
                          </div>

                          {/* CTA in expanded section */}
                          <div className="mt-8 pt-6 border-t border-amber-200/10 text-center">
                            {service.available ? (
                              <a 
                                href={
                                  service.id === 'community' ? 'https://t.me/communityfreefortal' : 
                                  service.id === 'pamm' ? 'https://t.me/+Qp7UCEncZ3Y5MzBk' : 
                                  '#'
                                } 
                                target={service.id === 'community' || service.id === 'pamm' ? '_blank' : '_self'}
                                rel={service.id === 'community' || service.id === 'pamm' ? 'noopener noreferrer' : ''}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button 
                                  variant="primary" 
                                  size="lg"
                                  className="animate-pulse-gold hover-lift glow-gold"
                                >
                                  <div className="w-5 h-5 flex items-center justify-center mr-2">
                                    <i className="ri-arrow-right-line"></i>
                                  </div>
                                  {service.id === 'community' && 'Accedi Subito alla Community Gratuita'}
                                  {service.id === 'formazione' && 'Inizia la Formazione Base'}
                                  {service.id === 'formazione-premium' && 'Entra in MastermindFX'}
                                  {service.id === 'pamm' && 'Richiedi Consulenza PAMM'}
                                  {service.id === 'copytrading' && 'Iscriviti alla Lista d\'Attesa'}
                                </Button>
                              </a>
                            ) : (
                              <Button 
                                variant="secondary" 
                                size="lg"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="w-5 h-5 flex items-center justify-center mr-2">
                                  <i className="ri-notification-line"></i>
                                </div>
                                Ricevi Notifica al Lancio
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 bg-slate-900/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 animate-slide-in-up">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Confronta i Servizi
              </h2>
              <p className="text-lg text-slate-300">
                Trova la soluzione più adatta alle tue esigenze di trading
              </p>
            </div>

            <Card variant="glass" className="overflow-hidden animate-scale-in glass-gradient">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-amber-200/20">
                      <th className="text-left py-4 px-6 text-slate-400 font-medium">Caratteristiche</th>
                      <th className="text-center py-4 px-6 text-amber-400 font-medium">Community</th>
                      <th className="text-center py-4 px-6 text-amber-400 font-medium">Formazione</th>
                      <th className="text-center py-4 px-6 text-amber-400 font-medium">MastermindFX</th>
                      <th className="text-center py-4 px-6 text-amber-400 font-medium">PAMM</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-amber-200/10">
                      <td className="py-4 px-6 text-slate-300">Accesso gratuito</td>
                      <td className="text-center py-4 px-6"><i className="ri-check-line text-green-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-check-line text-green-400"></i></td>
                    </tr>
                    <tr className="border-b border-amber-200/10">
                      <td className="py-4 px-6 text-slate-300">Formazione strutturata</td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-check-line text-green-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-check-line text-green-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                    </tr>
                    <tr className="border-b border-amber-200/10">
                      <td className="py-4 px-6 text-slate-300">Coach dedicato</td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-check-line text-green-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                    </tr>
                    <tr className="border-b border-amber-200/10">
                      <td className="py-4 px-6 text-slate-300">Gestione capitale</td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-check-line text-green-400"></i></td>
                    </tr>
                    <tr>
                      <td className="py-4 px-6 text-slate-300">Supporto 1-to-1</td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-check-line text-green-400"></i></td>
                      <td className="text-center py-4 px-6"><i className="ri-close-line text-red-400"></i></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 animate-slide-in-up">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Domande Frequenti
              </h2>
              <p className="text-lg text-slate-300">
                Risposte alle domande più comuni sui nostri servizi
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} variant="glass" className="hover-lift animate-slide-in-up glass-gradient" style={{animationDelay: `${index * 0.1}s`}}>
                  <h3 className="text-xl font-bold text-white mb-4 flex items-start">
                    <div className="w-6 h-6 flex items-center justify-center mr-3 mt-1">
                      <i className="ri-question-line text-amber-400"></i>
                    </div>
                    {faq.question}
                  </h3>
                  <p className="text-slate-300 ml-9">{faq.answer}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-slate-900/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Card variant="glass" className="text-center animate-bounce-in glass-gradient glow-gold-strong">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Inizia il Tuo Percorso di Trading
              </h2>
              <p className="text-lg text-slate-300 mb-8">
                Scegli il servizio più adatto alle tue esigenze e inizia a fare trading 
                con il supporto di una community professionale.
              </p>
              <div className="flex justify-center">
                <Button variant="secondary" size="lg" href="/risultati" className="hover-lift">
                  Vedi i Risultati
                </Button>
              </div>
            </Card>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
